/**
 * Phase F11 FE-1 — proctor lifecycle event hook.
 *
 * Subscribes to the browser events the F12 ML cheat-detection model
 * cares about and forwards them to the BE via the per-attempt
 * :class:`ProctorClient`.
 *
 * Engaged ONLY when the attempt's proctor profile is active — the
 * caller passes `enabled` so this hook is a no-op for `proctor_profile=off`
 * attempts (no listeners attached, no work done).
 *
 * Tracked events (mapped to ``PublicProctorEventKind``):
 *   * ``visibilitychange`` document → tab_blurred / tab_focused
 *     (paired so the F12 model can compute "time spent off-tab").
 *   * window blur/focus → tab_blurred / tab_focused (some browsers
 *     fire only one of the two; we listen to both and de-dupe with
 *     a short cooldown so we don't double-record).
 *   * ``mouseleave`` / ``mouseenter`` on document → mouse_left_window
 *     / mouse_returned. Fires on the document root so OS-window
 *     boundary crossings count.
 *   * ``paste`` / ``copy`` / ``contextmenu`` (right-click) blocking
 *     when the strict profile asks for it — this hook persists the
 *     events; the calling component is responsible for actually
 *     `preventDefault()`-ing the underlying user action.
 *   * ``fullscreenchange`` → fullscreen_entered / fullscreen_exited.
 *
 * Strict mode behaviour:
 *   When `mode === "strict"` the hook emits the `*_BLOCKED` events on
 *   paste / copy / right-click attempts AND calls `preventDefault()` on
 *   the underlying browser event. The lenient mode (default) only logs
 *   without preventing — useful for "soft" profiles where a teacher
 *   wants telemetry but doesn't want the applicant to feel locked out.
 */
import * as React from "react";

import type { ProctorClient } from "@/lib/proctor-client";

export type ProctorLifecycleMode = "strict" | "lenient";

export interface UseProctorLifecycleArgs {
  client: ProctorClient;
  /**
   * Set to false when the proctor profile is `off` — the hook is a
   * no-op and never attaches listeners.
   */
  enabled: boolean;
  /**
   * Strict profiles call `preventDefault()` on paste/copy/right-click
   * AND record a `*_BLOCKED` event. Lenient profiles only record.
   */
  mode?: ProctorLifecycleMode;
}

/**
 * Wire DOM lifecycle events to the proctor client. The hook returns
 * the count of events fired in this session — purely advisory, used
 * by the F12 strict-profile UI to surface a "you have left the
 * window N times" hint to the applicant.
 */
export function useProctorLifecycle({
  client,
  enabled,
  mode = "lenient",
}: UseProctorLifecycleArgs): { tabBlurredCount: number; mouseLeftCount: number } {
  const [tabBlurredCount, setTabBlurredCount] = React.useState(0);
  const [mouseLeftCount, setMouseLeftCount] = React.useState(0);

  // Cooldown trackers so a single user action that fires both
  // visibilitychange + window.blur doesn't double-record. 300ms is
  // generous-but-imperceptible.
  const lastTabBlurAtRef = React.useRef(0);
  const lastTabFocusAtRef = React.useRef(0);

  React.useEffect(() => {
    if (!enabled) return;

    const COOLDOWN_MS = 300;

    const fireBlur = () => {
      const now = Date.now();
      if (now - lastTabBlurAtRef.current < COOLDOWN_MS) return;
      lastTabBlurAtRef.current = now;
      setTabBlurredCount((n) => n + 1);
      void client.recordEvent("tab_blurred");
    };
    const fireFocus = () => {
      const now = Date.now();
      if (now - lastTabFocusAtRef.current < COOLDOWN_MS) return;
      lastTabFocusAtRef.current = now;
      void client.recordEvent("tab_focused");
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") fireBlur();
      else fireFocus();
    };
    const onWindowBlur = () => fireBlur();
    const onWindowFocus = () => fireFocus();

    const onMouseLeave = (event: MouseEvent) => {
      // Only count "leaves" past the document root — child element
      // mouseleaves bubble up here and would otherwise be noisy.
      if (event.relatedTarget !== null) return;
      setMouseLeftCount((n) => n + 1);
      void client.recordEvent("mouse_left_window");
    };
    const onMouseEnter = (event: MouseEvent) => {
      if (event.relatedTarget !== null) return;
      void client.recordEvent("mouse_returned");
    };

    const onPaste = (event: ClipboardEvent) => {
      if (mode === "strict") event.preventDefault();
      void client.recordEvent("paste_blocked", { strict: mode === "strict" });
    };
    const onCopy = (event: ClipboardEvent) => {
      if (mode === "strict") event.preventDefault();
      void client.recordEvent("copy_blocked", { strict: mode === "strict" });
    };
    const onContextMenu = (event: MouseEvent) => {
      if (mode === "strict") event.preventDefault();
      void client.recordEvent("right_click_blocked", { strict: mode === "strict" });
    };

    const onFullscreenChange = () => {
      const fs = document.fullscreenElement !== null;
      void client.recordEvent(fs ? "fullscreen_entered" : "fullscreen_exited");
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onWindowBlur);
    window.addEventListener("focus", onWindowFocus);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    document.documentElement.addEventListener("mouseenter", onMouseEnter);
    document.addEventListener("paste", onPaste);
    document.addEventListener("copy", onCopy);
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("fullscreenchange", onFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onWindowBlur);
      window.removeEventListener("focus", onWindowFocus);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      document.documentElement.removeEventListener("mouseenter", onMouseEnter);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [client, enabled, mode]);

  return { tabBlurredCount, mouseLeftCount };
}
